/**
 * The layout every console view stands on, ported from the AI Workbench's Settings (SettingsGroup.tsx,
 * rows.tsx, ViewExplainer.tsx, `.settings-card`). Styles in src/layout.css; the type scale in
 * src/index.css. Art.tsx dresses a view (ViewHeader, ArtStat, ArtSection, ArtEmpty); this file lays
 * it out.
 *
 *   PageColumn      the view's column: `form` 720px centred (settings, forms, one record), `data`
 *                   1440px centred (lists, tables, boards), `full` (canvases, maps)
 *   SectionHeading  a small bold heading (13px/600), a muted line under it (12px), actions at the end
 *   ListGroup       a SectionHeading (with an optional `count` pill), then ONE rounded card whose rows
 *                   are divided by hairlines; `empty` (an ArtEmpty) stands in place of the card
 *   ListRow         icon?, title (13px/500) and description (12px muted), the control at the
 *                   trailing end; or a whole row you press (to / onPress) with a chevron. `status` (a
 *                   StatusMark) sits before the control and under the words on a narrow row;
 *                   `compactControl` keeps a menu at the end on a narrow row instead of under the words
 *   ListBlock       free-form content in a group (a note, a row of buttons, a table with `flush`)
 *   Explainer       "How X works": one line in the view's tone that opens to a few sentences
 *   SubnavLayout    grouped section links in a column beside the content, a picker on narrow
 *
 * Beside these, in their own files:
 *
 *   RowMenu         (RowMenu.tsx) the one "more" control a row may carry: an ellipsis and a short menu
 *                   (Edit, Download, Archive, Remove…) instead of a button for each
 *   FormSheet       (FormSheet.tsx) adding or editing one thing from a list: a sheet from the trailing
 *                   edge sized to the form (md to 2xl), Cancel and the one primary in its foot; with
 *                   FormField, a label above its control and a muted hint under it
 *   SearchBox       (SearchBox.tsx) the search of a view's filter bar: in the ViewHeader's actions
 *                   with at most two choices beside it (a period, a kind); the view's other actions
 *                   go to the section they act on
 *   .data-table     (layout.css) columns that are compared, in a `flush` ListBlock: a muted header
 *                   row, hairlines between rows, a row you press opens its detail in a sheet; a
 *                   column that matters less is hidden below a width, and the table scrolls inside
 *                   its card rather than the page
 *
 * The rules, for every view:
 *
 *  1. Type: text-sm (13px) for content and row titles, text-xs (12px) for descriptions, meta and nav,
 *     text-2xs (11px) for counts and tiny labels. Controls are HeroUI's 14px. Never `text-[Npx]`,
 *     never a font family of a view's own.
 *  2. Widths: a view is a PageColumn. Settings, forms and a single record use `form` (45rem); lists,
 *     tables, boards and dashboards use `data` (90rem); only a canvas or a map uses `full`. Centred.
 *  3. Gutters: the shell's <main> gives 16px on a phone and 24px from 768px up. A view adds none of
 *     its own at the page edge, and nothing scrolls sideways: a wide table scrolls inside its card.
 *  4. Breakpoints: the window's are Tailwind's (sm 640, md 768, lg 1024) and only the shell uses
 *     them. Inside a view, layouts answer to their container: rows put their control under the text
 *     below 35rem (560px); a SubnavLayout is a column at 48rem (768px) and up, a picker below. The
 *     PageColumn is the container `page-column`: an `.art-stats` tile left alone on its last row
 *     takes the row's width there, so a narrow view has no orphan stat.
 *  5. Settings and forms are calm: a ViewHeader, then ListGroups of ListRows. Switches sit at the
 *     trailing end; a choice that applies at once saves at once (no Save button for a switch or a
 *     select); a draft of several fields gets one Save in the group's last row. No artwork inside a
 *     group, no card inside a card, no badge on every row; status is a StatusMark (dot + word).
 *  6. Headings are sentence case and short ("Date and time"), as the workbench writes them.
 */

import { ChevronDown, ChevronRight, CircleQuestion, Xmark } from '@gravity-ui/icons';
import { Button, Header, ListBox, Select } from '@heroui/react';
import { type ReactNode, useId, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { ArtTone } from './Art.tsx';
import { Hint } from './Hint.tsx';

/* ---------- the page column ---------- */

export type PageWidth = 'form' | 'data' | 'full';

/** A view's column: header band, stats, then sections, with the standard width for what it holds. */
export function PageColumn({ width = 'data', className, children }: { width?: PageWidth; className?: string; children: ReactNode }) {
  return (
    <div className={`page-column ${className ?? ''}`} data-width={width}>
      {children}
    </div>
  );
}

/* ---------- headings and groups ---------- */

/** A small bold heading with a muted line under it; actions align with the title. */
export function SectionHeading({ title, count, description, actions, id }: { title?: ReactNode; count?: ReactNode; description?: ReactNode; actions?: ReactNode; id?: string }) {
  const counted = count !== undefined && count !== null && count !== '';
  return (
    <div className="section-heading">
      <div className="section-heading__text">
        {title ? (
          <h2 className="section-heading__title" id={id}>
            {title}
            {counted ? <span className="section-heading__count">{typeof count === 'number' ? count.toLocaleString() : count}</span> : null}
          </h2>
        ) : null}
        {description ? <p className="section-heading__description">{description}</p> : null}
      </div>
      {actions ? <div className="section-heading__actions">{actions}</div> : null}
    </div>
  );
}

/**
 * A group of rows: the heading outside (leave `title` out when the page header already names the
 * group, and keep the description), one card inside with hairlines between its children. Every
 * direct child is padded by the card (a ListRow, a ListBlock, or any element), so put rows straight
 * in, not inside a wrapper. `id` is the anchor a link or a search lands on (`#settings-<id>`).
 * `count` is a quiet pill after the title (how many rows, how many wait); `empty`, when given, is
 * shown in place of the card (an ArtEmpty), so an empty list is never a card inside a card.
 */
export function ListGroup({ id, title, count, description, actions, empty, children, className }: { id?: string; title?: ReactNode; count?: ReactNode; description?: ReactNode; actions?: ReactNode; empty?: ReactNode; children?: ReactNode; className?: string }) {
  const headingId = useId();
  return (
    <section id={id ? `settings-${id}` : undefined} className={`list-group-section ${className ?? ''}`} aria-labelledby={title ? headingId : undefined}>
      {title || description || actions ? <SectionHeading id={headingId} title={title} count={count} description={description} actions={actions} /> : null}
      {empty ? empty : <div className="list-group">{children}</div>}
    </section>
  );
}

/** Free-form content in a group: a note, a row of buttons, a list. `flush` drops the padding for a table or a canvas. */
export function ListBlock({ children, flush, className }: { children: ReactNode; flush?: boolean; className?: string }) {
  return <div className={`${flush ? 'list-block--flush' : ''} ${className ?? ''}`}>{children}</div>;
}

/* ---------- a row ---------- */

interface ListRowProps {
  title: ReactNode;
  description?: ReactNode;
  /** A 16px outline icon, in a quiet well at the start. Rows in one group either all have one or none do. */
  icon?: ReactNode;
  /** The trailing control: a Toggle, a ChoiceSelect, a Button, a StatusMark. Below 35rem it goes under the text (a switch stays put). */
  control?: ReactNode;
  /** The control is small (a RowMenu, one short button): it stays at the trailing end on a narrow row instead of going under the text. */
  compactControl?: boolean;
  /** The row's state (a StatusMark): before the control (or the chevron) at the trailing end, and under the description when the row is narrower than 35rem. */
  status?: ReactNode;
  /** A plain value at the trailing end instead of a control (a choice someone else made). */
  value?: ReactNode;
  /** The whole row is a link: it gets a chevron and a hover. */
  to?: string;
  /** The whole row is a button: it gets a chevron and a hover. */
  onPress?: () => void;
  /** With `to` or `onPress`: something before the chevron (a count, a StatusMark). */
  trailing?: ReactNode;
  /** Renders the title as a <label> for the field with this id. */
  htmlFor?: string;
  /** Full-width content under the text: a second field, a short list. */
  children?: ReactNode;
  id?: string;
}

/**
 * One row of a ListGroup. Paints nothing of its own (the group owns the padding and the hairlines),
 * so a list of settings is read, not pointed at; only a row you press has a hover.
 */
export function ListRow({ title, description, icon, control, compactControl, status, value, to, onPress, trailing, htmlFor, children, id }: ListRowProps) {
  const Title = htmlFor ? 'label' : 'span';
  const text = (
    <span className="list-row__text">
      <Title className="list-row__title" {...(htmlFor ? { htmlFor } : {})}>
        {title}
      </Title>
      {description ? <span className="list-row__description block">{description}</span> : null}
      {status ? <span className="list-row__status">{status}</span> : null}
    </span>
  );
  const iconWell = icon ? (
    <span className="list-row__icon" aria-hidden="true">
      {icon}
    </span>
  ) : null;
  if (to || onPress) {
    const inner = (
      <>
        {iconWell}
        {text}
        {trailing || status ? (
          <span className="list-row__control">
            {status ? <span className="list-row__status-end">{status}</span> : null}
            {trailing}
          </span>
        ) : null}
        <ChevronRight className="list-row__chevron" aria-hidden="true" />
      </>
    );
    return to ? (
      <Link to={to} id={id} className="list-row flex-nowrap" data-pressable>
        {inner}
      </Link>
    ) : (
      <button type="button" id={id} className="list-row flex-nowrap" data-pressable onClick={onPress}>
        {inner}
      </button>
    );
  }
  const compact = (control && compactControl) || (status && !control) ? true : undefined;
  return (
    <div className="list-row" id={id} data-has-control={control || status ? true : undefined} data-compact-control={compact}>
      {iconWell}
      {text}
      {control || status ? (
        <div className="list-row__control">
          {status ? <span className="list-row__status-end">{status}</span> : null}
          {control}
        </div>
      ) : value !== undefined ? (
        <span className="list-row__value">{value}</span>
      ) : null}
      {children ? <div className="list-row__body">{children}</div> : null}
    </div>
  );
}

/* ---------- "How it works" ---------- */

type ExplainerState = 'closed' | 'open' | 'hidden';
const EXPLAINER_KEY = 'aioe.explainers';

function readExplainers(): Record<string, ExplainerState> {
  try {
    return JSON.parse(localStorage.getItem(EXPLAINER_KEY) ?? '{}') as Record<string, ExplainerState>;
  } catch {
    return {};
  }
}

/**
 * "How X works": one line on the view's tone, closed by default, that opens to three to five
 * sentences on what the page decides and when you would come here. "Don't show again" leaves one
 * quiet "How it works" button in its place. Whether it is open is remembered per `id` in this browser.
 */
export function Explainer({ id, tone, icon, title, summary, children }: { id: string; tone: ArtTone; icon: ReactNode; title: string; summary: ReactNode; children: ReactNode }) {
  const [state, setState] = useState<ExplainerState>(() => readExplainers()[id] ?? 'closed');
  const set = (next: ExplainerState) => {
    setState(next);
    try {
      localStorage.setItem(EXPLAINER_KEY, JSON.stringify({ ...readExplainers(), [id]: next }));
    } catch {
      /* storage unavailable: it is remembered for this visit only */
    }
  };
  const bodyId = useId();
  if (state === 'hidden') {
    return (
      <div className="explainer-hidden">
        <Button size="sm" variant="ghost" className="text-muted" onPress={() => set('closed')}>
          <CircleQuestion className="size-3.5" aria-hidden="true" />
          How it works
        </Button>
      </div>
    );
  }
  const open = state === 'open';
  return (
    <section className="card-art explainer" data-tone={tone} data-motif="dots" data-open={open || undefined} aria-label={title}>
      <div className="explainer__bar">
        <button type="button" className="explainer__toggle" aria-expanded={open} aria-controls={open ? bodyId : undefined} onClick={() => set(open ? 'closed' : 'open')}>
          <span className="card-art__icon explainer__icon">{icon}</span>
          <span className="explainer__heading">
            <span className="explainer__title">{title}</span>
            {open ? null : <span className="explainer__summary">{summary}</span>}
          </span>
          <ChevronDown className="explainer__chevron" aria-hidden="true" />
        </button>
        <Hint text="Don't show again. A How it works button stays in its place.">
          <Button size="sm" variant="ghost" isIconOnly aria-label="Don't show again" className="text-muted" onPress={() => set('hidden')}>
            <Xmark className="size-3.5" />
          </Button>
        </Hint>
      </div>
      {open ? (
        <div className="explainer__body" id={bodyId}>
          {children}
        </div>
      ) : null}
    </section>
  );
}

/* ---------- a secondary nav ---------- */

export interface SubnavItem {
  key: string;
  label: string;
  /** Where the link goes. */
  to: string;
}

export interface SubnavGroup {
  label: string;
  items: SubnavItem[];
}

/**
 * A view with sections of its own (Settings, a project): grouped links in a 12rem column beside the
 * content from 48rem of width up, and one picker above the content below that. Links are real
 * addresses, so a section can be bookmarked, opened in a new tab and reached with Back.
 */
export function SubnavLayout({ label, groups, current, children }: { label: string; groups: SubnavGroup[]; current: string; children: ReactNode }) {
  const navigate = useNavigate();
  const items = groups.flatMap((g) => g.items);
  return (
    <div className="subnav-layout">
      <div className="subnav-layout__inner">
        <nav className="subnav" aria-label={label}>
          {groups.map((group) => (
            <div key={group.label} className="subnav__group">
              {group.label ? <p className="subnav__group-label">{group.label}</p> : null}
              {group.items.map((item) => (
                <Link key={item.key} to={item.to} className="subnav__link" aria-current={item.key === current ? 'page' : undefined}>
                  {item.label}
                </Link>
              ))}
            </div>
          ))}
        </nav>
        <div className="subnav-picker">
          <Select
            aria-label={label}
            variant="secondary"
            className="w-full"
            value={current}
            onChange={(k) => {
              const item = items.find((i) => i.key === k);
              if (item) navigate(item.to);
            }}
          >
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {groups.map((group) => (
                  <ListBox.Section key={group.label || 'top'}>
                    {group.label ? <Header>{group.label}</Header> : null}
                    {group.items.map((item) => (
                      <ListBox.Item key={item.key} id={item.key} textValue={item.label}>
                        {item.label}
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                    ))}
                  </ListBox.Section>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
        </div>
        <div className="subnav-layout__content">{children}</div>
      </div>
    </div>
  );
}
